import {
  Component,
  Input,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  SelectComponent,
  SelectOption,
} from '@app/shared/components/select/select.component';

import {
  FieldAddedEvent,
  makeUpsertVM,
  UpsertViewModel,
} from './upsert-vm.factory';
import { BusinessRuleFacade } from '@app/business-rules/facades';
import { BusinessRulesApiService } from '@app/business-rules/services';
import {
  BusinessRule,
  PredicateFormGroup,
  RuleTypes,
  StaticAttributes,
} from '@app/business-rules/model';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AttributeDataType } from '@dg/attributes-data-access';
import { ComboboxComponent } from '@app/shared/components/combobox/combobox.component';
import { NotificationType } from '@lib/fresco';

@Component({
  selector: 'dgx-business-rule-upsert-modal',
  templateUrl: './business-rule-upsert-modal.component.html',
  styleUrls: ['./business-rule-upsert-modal.scss'],
})

/**
 * Note: Action value fields MUST manage their own a11y label states.  Due to the mix of reuse and
 *   new fields, this modal wrapper cannot assume anything about the label state of its children.
 */
export class BusinessRuleUpsertModalComponent implements OnInit, OnDestroy {
  @Input() public businessRuleId?: number;
  @Input() public orgId: number;
  // Need to be able to pass this to deal with things coming from outside our routing.
  @Input() public initialRuleType: RuleTypes;
  @Input() public refreshPaginationOnSubmit: boolean = true;

  @ViewChildren('predicateNameField')
  public predicateNameFields: QueryList<ElementRef<ComboboxComponent>>;

  @ViewChildren('outcomeDropdown')
  public outcomeDropdownWrapper: QueryList<SelectComponent>;

  public readonly NotificationType = NotificationType;
  public isLoading = false;
  public submitAttempted = false;
  public vm: UpsertViewModel;
  public readonly AttributeDataType = AttributeDataType;
  public ruleIsInitiallyInvalid: boolean;
  public readonly booleanOptions = [
    { name: 'True', value: 'True' },
    { name: 'False', value: 'False' },
  ];
  private onDestroy = new Subject<void>();

  constructor(
    private activeModal: NgbActiveModal,
    private translate: TranslateService,
    private facade: BusinessRuleFacade,
    private businessRulesService: BusinessRulesApiService,
    private changeDetectorRef: ChangeDetectorRef,
    private rulesApiService: BusinessRulesApiService
  ) {}

  /**
   * Disable the submit button in following cases
   * 1. If the form is submitted and awaiting response `isLoading`
   * 2. If form status is PENDING (async validator is not resolved yet)
   */
  get isSubmitDisabled(): boolean {
    return this.isLoading;
  }

  public ngOnInit(): void {
    if (this.initialRuleType) {
      this.facade.setRuleType(this.initialRuleType);
    }
    this.rulesApiService
      .loadAttributeNames(this.orgId, this.facade.currentRuleType)
      .subscribe((attributes) => {
        this.vm = makeUpsertVM(
          this.facade,
          this.translate,
          this.businessRulesService,
          this.orgId,
          this.refreshPaginationOnSubmit,
          attributes,
          this.businessRuleId
        );
        // The ASYNC validator does not display error when API response is delivered after user has left the field.
        this.vm.form
          .get('ruleName')
          .statusChanges.pipe(distinctUntilChanged(), takeUntil(this.onDestroy))
          .subscribe(() => {
            this.changeDetectorRef.detectChanges();
          });

        // To auto trigger 'focus()' changes, add callback to be notified when fields are added
        // TODO: - should UI activity occur when fields are removed or updated?
        this.vm.onFieldAdded(({ fieldType }) => {
          switch (fieldType) {
            case FieldAddedEvent.OUTCOME:
              this.outcomeDropdownWrapper?.last.trigger.nativeElement.focus();
              break;
            case FieldAddedEvent.PREDICATE:
              const comboBox = this.predicateNameFields
                ?.last as unknown as ComboboxComponent;
              comboBox?.inputElement.nativeElement.focus();
              break;
            default:
              break;
          }
        });

        this.ruleIsInitiallyInvalid = this.businessRuleId
          ? this.vm.ruleIsInvalid(this.vm.form)
          : false;
      });
  }

  public dismiss(): void {
    this.activeModal.dismiss();
  }

  public submit() {
    this.submitAttempted = true;
    if (this.vm.form.invalid) {
      return;
    }

    this.isLoading = true;

    this.vm.submit((r: BusinessRule) => {
      this.isLoading = false;
      this.activeModal.close(r);
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  public getOptions(
    attributeOptions: SelectOption[],
    attributeIndex: number,
    existingPredicates: PredicateFormGroup[]
  ): () => SelectOption[] {
    return () => {
      const isNewIsSelected = existingPredicates.some(
        (x) => x.get('fieldName').value === StaticAttributes.APPLY_TO_NEW_USERS
      );

      if (attributeIndex == 0 && existingPredicates.length == 1) {
        return attributeOptions;
      } else if (isNewIsSelected) {
        return attributeOptions.filter(
          (x) =>
            x.id !== StaticAttributes.APPLY_TO_ALL_USERS &&
            x.id !== StaticAttributes.APPLY_TO_NEW_USERS
        );
      } else {
        return attributeOptions.filter(
          (x) => x.id !== StaticAttributes.APPLY_TO_ALL_USERS
        );
      }
    };
  }
}
